import {
  CLINICAL_CONSULTATION_ACTION,
  DISCHARGE_LETTER_FORM_NAMES,
} from "core/consts";
import { getClinicalConsultationActions } from "core/model/clinicalConsultations";
import {
  ClinicalConsultation,
  ClinicalConsultationAction,
  RecareFileType,
  SearchAction,
  ValueOf,
} from "core/types";
import Translations from "translations/types";
import { FORM_NAME_TO_FILE_TYPE } from "./Modal/formNameToFileType";
import { MigratedForms } from "./Modal/utils";

export const MIN_CREATE_PATIENT_EXTERNAL_ID_LENGTH = 5;

export const DOCS_DEEP_LINK_VALIDATION_REASON = {
  INVALID_EXTERNAL_ID: "invalid_external_id",
  INVALID_FORM: "invalid_form",
  MISSING_EXTERNAL_ID: "missing_external_id",
} as const;

export type DocsDeepLinkValidationReason = ValueOf<
  typeof DOCS_DEEP_LINK_VALIDATION_REASON
>;

export type DocsDeepLinkValidationResult =
  | {
      externalId: string;
      formName: MigratedForms | null;
      status: "ok";
    }
  | {
      reason: DocsDeepLinkValidationReason;
      status: "error";
    };

export function parseDischargeLetterDeepLinkForm(
  value: string | null,
): MigratedForms | null {
  if (value == null) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  for (const formName of DISCHARGE_LETTER_FORM_NAMES) {
    if (formName === trimmed) {
      return formName;
    }
  }

  return null;
}

export function validateDocsDeepLinkParams({
  externalId,
  formSlug,
}: {
  externalId: string | null;
  formSlug: string | null;
}): DocsDeepLinkValidationResult {
  const trimmedExternalId = externalId?.trim() ?? "";
  const hasFormParam = formSlug != null && formSlug.trim().length > 0;

  if (!hasFormParam) {
    if (trimmedExternalId.length >= MIN_CREATE_PATIENT_EXTERNAL_ID_LENGTH) {
      return {
        externalId: trimmedExternalId,
        formName: null,
        status: "ok",
      };
    }

    if (trimmedExternalId.length > 0) {
      return {
        reason: DOCS_DEEP_LINK_VALIDATION_REASON.INVALID_EXTERNAL_ID,
        status: "error",
      };
    }

    return {
      externalId: "",
      formName: null,
      status: "ok",
    };
  }

  const parsedForm = parseDischargeLetterDeepLinkForm(formSlug);
  if (parsedForm == null) {
    return {
      reason: DOCS_DEEP_LINK_VALIDATION_REASON.INVALID_FORM,
      status: "error",
    };
  }

  if (!trimmedExternalId) {
    return {
      reason: DOCS_DEEP_LINK_VALIDATION_REASON.MISSING_EXTERNAL_ID,
      status: "error",
    };
  }

  if (trimmedExternalId.length < MIN_CREATE_PATIENT_EXTERNAL_ID_LENGTH) {
    return {
      reason: DOCS_DEEP_LINK_VALIDATION_REASON.INVALID_EXTERNAL_ID,
      status: "error",
    };
  }

  return {
    externalId: trimmedExternalId,
    formName: parsedForm,
    status: "ok",
  };
}

function isFormActiveForRecareFileTypes(
  action: SearchAction | ClinicalConsultationAction,
  formTypes: RecareFileType[],
): boolean {
  return (
    !action.context?.disabled &&
    formTypes.some((formType) => action.context?.values?.includes(formType))
  );
}

export function canOpenDeepLinkDischargeForm({
  consultation,
  formName,
}: {
  consultation: ClinicalConsultation | null | undefined;
  formName: MigratedForms;
}): boolean {
  const generateFormAction = getClinicalConsultationActions(
    consultation ?? null,
  )[CLINICAL_CONSULTATION_ACTION.GENERATE_FORM];

  if (!generateFormAction) {
    return false;
  }

  return isFormActiveForRecareFileTypes(generateFormAction, [
    FORM_NAME_TO_FILE_TYPE[formName],
  ]);
}

export function getDocsDeepLinkErrorMessage(
  translations: Translations,
  reason: DocsDeepLinkValidationReason,
): string {
  switch (reason) {
    case DOCS_DEEP_LINK_VALIDATION_REASON.INVALID_FORM:
      return translations.insuranceApp.eligibilityTab.approvalFlow
        .invalidLinkText;
    case DOCS_DEEP_LINK_VALIDATION_REASON.MISSING_EXTERNAL_ID:
    case DOCS_DEEP_LINK_VALIDATION_REASON.INVALID_EXTERNAL_ID:
      return translations.patient.kis.externalIdMinCharactersError({
        numChars: String(MIN_CREATE_PATIENT_EXTERNAL_ID_LENGTH),
      });
    default:
      return translations.general.error;
  }
}
